/**
 * Klaviyo Custom Popup - Multi-Step Flow Controller
 * Handles form display, validation, API submission, and state management
 * 
 * @version 1.0.0
 * @author Northern Fitness Supply
 */

const KLAVIYO_PHONE_COUNTRY_CODES = [
  'AF', 'AX', 'AL', 'DZ', 'AS', 'AD', 'AO', 'AI', 'AG', 'AR', 'AM', 'AW', 'AC', 'AU', 'AT', 'AZ',
  'BS', 'BH', 'BD', 'BB', 'BY', 'BE', 'BZ', 'BJ', 'BM', 'BT', 'BO', 'BQ', 'BA', 'BW', 'BR', 'IO',
  'BN', 'BG', 'BF', 'BI', 'KH', 'CM', 'CA', 'CV', 'KY', 'CF', 'TD', 'CL', 'CN', 'CX', 'CC', 'CO',
  'KM', 'CG', 'CD', 'CK', 'CR', 'CI', 'HR', 'CU', 'CW', 'CY', 'CZ', 'DK', 'DJ', 'DM', 'DO', 'EC',
  'EG', 'SV', 'GQ', 'ER', 'EE', 'ET', 'FK', 'FO', 'FM', 'FJ', 'FI', 'FR', 'GF', 'PF', 'GA', 'GM',
  'GE', 'DE', 'GH', 'GI', 'GR', 'GL', 'GD', 'GP', 'GU', 'GT', 'GG', 'GN', 'GW', 'GY', 'HT', 'VA',
  'HN', 'HK', 'HU', 'IS', 'IN', 'ID', 'IR', 'IQ', 'IE', 'IM', 'IL', 'IT', 'JM', 'JP', 'JE', 'JO',
  'KZ', 'KE', 'KI', 'XK', 'KW', 'KG', 'LA', 'LV', 'LB', 'LS', 'LR', 'LY', 'LI', 'LT', 'LU', 'MO',
  'MG', 'MW', 'MY', 'MV', 'ML', 'MT', 'MH', 'MQ', 'MR', 'MU', 'YT', 'MX', 'MD', 'MC', 'MN', 'ME',
  'MS', 'MA', 'MZ', 'MM', 'NA', 'NR', 'NP', 'NL', 'NC', 'NZ', 'NI', 'NE', 'NG', 'NU', 'NF', 'KP',
  'MK', 'MP', 'NO', 'OM', 'PK', 'PW', 'PS', 'PA', 'PG', 'PY', 'PE', 'PH', 'PL', 'PT', 'PR', 'QA',
  'RE', 'RO', 'RU', 'RW', 'BL', 'SH', 'KN', 'LC', 'MF', 'PM', 'VC', 'WS', 'SM', 'ST', 'SA', 'SN',
  'RS', 'SC', 'SL', 'SG', 'SX', 'SK', 'SI', 'SB', 'SO', 'ZA', 'KR', 'SS', 'ES', 'LK', 'SD', 'SR',
  'SJ', 'SZ', 'SE', 'CH', 'SY', 'TW', 'TJ', 'TZ', 'TH', 'TL', 'TG', 'TK', 'TO', 'TT', 'TA', 'TN',
  'TR', 'TM', 'TC', 'TV', 'UG', 'UA', 'AE', 'GB', 'US', 'UY', 'UZ', 'VU', 'VE', 'VN', 'VG', 'VI',
  'WF', 'EH', 'YE', 'ZM', 'ZW'
];

const KLAVIYO_PHONE_COUNTRY_LABELS = {
  AC: 'Ascension Island',
  BQ: 'Bonaire, Sint Eustatius and Saba',
  CD: 'Congo, Democratic Republic of the',
  CG: 'Congo',
  CI: "Cote d'Ivoire",
  CW: 'Curaçao',
  FM: 'Federated States of Micronesia',
  FK: 'Falkland Islands',
  GF: 'French Guiana',
  HK: 'Hong Kong',
  IR: 'Iran',
  KP: 'North Korea',
  KR: 'South Korea',
  MF: 'Saint Martin (French Part)',
  MK: 'North Macedonia',
  MO: 'Macao',
  PF: 'French Polynesia',
  PS: 'Palestine',
  RE: 'Reunion',
  SH: 'Saint Helena',
  SJ: 'Svalbard and Jan Mayen',
  SX: 'Sint Maarten',
  TA: 'Tristan da Cunha',
  TW: 'Taiwan',
  VA: 'Holy See (Vatican City State)',
  VG: 'Virgin Islands, British',
  VI: 'Virgin Islands, U.S.',
  XK: 'Kosovo',
  AX: 'Aland Islands'
};

/** Persist multi-step funnel across reloads (same browser, same origin) */
const KLAVIYO_FLOW_LS_STEP = 'klaviyo_popup_max_step';
const KLAVIYO_FLOW_LS_EMAIL = 'klaviyo_popup_email';
const KLAVIYO_FLOW_LS_INTEREST = 'klaviyo_popup_product_interest';

class KlaviyoPopupController {
  constructor(config) {
    this.config = {
      klaviyoPublicKey: config.klaviyoPublicKey || '',
      klaviyoEmailListId: config.klaviyoEmailListId || '',
      klaviyoSmsListId: config.klaviyoSmsListId || '',
      klaviyoListId: config.klaviyoListId || '',
      triggers: config.triggers || {},
      targeting: config.targeting || {},
      frequency: config.frequency || {},
      ...config
    };

    /** Suffix appended to every element id, so a second popup variant (e.g. an A/B test) can run on the same page without id collisions. */
    this.idSuffix = config.idSuffix || '';
    this.ids = {
      overlay: `klaviyo-popup-overlay${this.idSuffix}`,
      emailForm: `klaviyo-email-form${this.idSuffix}`,
      smsForm: `klaviyo-sms-form${this.idSuffix}`,
      skipSms: `klaviyo-skip-sms${this.idSuffix}`,
      shopNow: `klaviyo-shop-now${this.idSuffix}`,
      phoneCountry: `klaviyo-phone-country${this.idSuffix}`,
      phoneInput: `klaviyo-phone-input${this.idSuffix}`,
      phoneError: `klaviyo-phone-error${this.idSuffix}`,
      emailInput: `klaviyo-email-input${this.idSuffix}`,
      emailError: `klaviyo-email-error${this.idSuffix}`
    };

    this.currentStep = 1;
    this.totalSteps = 4;
    this.userData = {};
    this.overlay = null;
    this.isInitialized = false;
    this.hasTriggered = false;
    /** @type {Element|null} Element to restore focus to when the dialog closes */
    this._focusReturnEl = null;
    /** @type {Promise<unknown>|null} */
    this._phoneLibLoadPromise = null;
    this._phoneLib = null;
    /** @type {(() => void)|null} */
    this._phoneCountrySync = null;
    this._aliaPhoneDomReady = false;
  }

  /** Id of a step wrapper (`klaviyo-step-1`, etc.), suffixed for this instance. */
  stepId(stepNumber) {
    return `klaviyo-step-${stepNumber}${this.idSuffix}`;
  }

  /**
   * Initialize the popup controller
   */
  init() {
    if (this.isInitialized) return;
    
    if (window.Shopify && window.Shopify.designMode) return;

    this.overlay = document.getElementById(this.ids.overlay);
    if (!this.overlay) {
      console.error('[Klaviyo Popup] Overlay element not found');
      return;
    }
    
    this.setupEventListeners();
    this.restoreFlowFromStorage();
    this.evaluateTriggers();
    this.isInitialized = true;
    
    console.log('[Klaviyo Popup] Initialized successfully');
  }

  /**
   * Clear saved funnel progress (after full completion or when completion cookie exists).
   */
  clearFlowLocalStorage() {
    try {
      localStorage.removeItem(KLAVIYO_FLOW_LS_STEP);
      localStorage.removeItem(KLAVIYO_FLOW_LS_EMAIL);
      localStorage.removeItem(KLAVIYO_FLOW_LS_INTEREST);
    } catch {
      /* private mode / blocked */
    }
  }

  persistSurveyComplete(interest) {
    try {
      localStorage.setItem(KLAVIYO_FLOW_LS_INTEREST, interest || 'not_specified');
      localStorage.setItem(KLAVIYO_FLOW_LS_STEP, '2');
    } catch {
      /* ignore */
    }
  }

  persistEmailComplete(email) {
    try {
      localStorage.setItem(KLAVIYO_FLOW_LS_EMAIL, email);
      localStorage.setItem(KLAVIYO_FLOW_LS_STEP, '3');
    } catch {
      /* ignore */
    }
  }

  persistReachedConfirmation() {
    try {
      localStorage.setItem(KLAVIYO_FLOW_LS_STEP, '4');
    } catch {
      /* ignore */
    }
  }

  /**
   * Restore saved step + userData after reload (before triggers run).
   */
  restoreFlowFromStorage() {
    if (this.config.testMode === true) return;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('klaviyo_preview') === 'true') return;
    } catch {
      /* ignore */
    }

    if (this.getCookie('klaviyo_popup_completed')) {
      this.clearFlowLocalStorage();
      return;
    }

    let maxStep;
    try {
      maxStep = parseInt(localStorage.getItem(KLAVIYO_FLOW_LS_STEP), 10);
    } catch {
      return;
    }
    if (!Number.isFinite(maxStep) || maxStep < 2 || maxStep > 4) return;
    // Past email: shouldDisplay() hides the popup; no need to mutate steps in the DOM
    if (maxStep >= 3) return;

    let interest;
    let email;
    try {
      interest = localStorage.getItem(KLAVIYO_FLOW_LS_INTEREST);
      email = localStorage.getItem(KLAVIYO_FLOW_LS_EMAIL);
    } catch {
      return;
    }
    if (interest) this.userData.product_interest = interest;
    if (email) this.userData.email = email;

    this.goToStep(maxStep);
  }

  handleSkipSms() {
    this.handleDismiss();
  }

  /**
   * Setup all event listeners for forms and buttons
   */
  setupEventListeners() {
    // Close button
    const closeBtn = this.overlay.querySelector('.klaviyo-popup-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.handleDismiss());
    }
    
    // Overlay click (close on outside click)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.handleDismiss();
      }
    });
    
    // Step 1: Survey buttons
    const surveyButtons = this.overlay.querySelectorAll('.klaviyo-survey-button');
    surveyButtons.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleSurveySelection(e));
    });

    // Step 2: Email form
    const emailForm = document.getElementById(this.ids.emailForm);
    if (emailForm) {
      emailForm.addEventListener('submit', (e) => this.handleEmailSubmit(e));
    }

    // Step 3: SMS form
    const smsForm = document.getElementById(this.ids.smsForm);
    if (smsForm) {
      smsForm.addEventListener('submit', (e) => this.handleSMSSubmit(e));
    }

    const skipSMSBtn = document.getElementById(this.ids.skipSms);
    if (skipSMSBtn) {
      skipSMSBtn.addEventListener('click', () => this.handleSkipSms());
    }

    // Step 4: Shop now button
    const shopNowBtn = document.getElementById(this.ids.shopNow);
    if (shopNowBtn) {
      shopNowBtn.addEventListener('click', () => this.complete());
    }
    
    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible()) {
        this.handleDismiss();
      }
    });

    // Trap Tab focus inside the dialog while it's open
    document.addEventListener('keydown', (e) => this.trapFocus(e));
  }

  /**
   * Focusable elements within the currently visible step (ignores elements
   * hidden in inactive steps or via display:none).
   */
  getFocusableElements() {
    if (!this.overlay) return [];
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(this.overlay.querySelectorAll(selector)).filter((el) => el.offsetParent !== null);
  }

  /**
   * Keep Tab/Shift+Tab cycling within the dialog while it's open
   */
  trapFocus(e) {
    if (e.key !== 'Tab' || !this.isVisible()) return;

    const focusable = this.getFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /**
   * Evaluate trigger rules and display popup if conditions are met
   */
  evaluateTriggers() {
    // Check if popup should display
    if (!this.shouldDisplay()) {
      console.log('[Klaviyo Popup] Display conditions not met');
      return;
    }
    
    if (this.hasTriggered) {
      return;
    }
    
    const triggers = this.config.triggers;
    
    // Test mode: Always show immediately
    if (this.config.testMode === true) {
      console.log('[Klaviyo Popup] Test mode - showing immediately');
      this.show();
      this.hasTriggered = true;
      return;
    }
    
    // Immediate display
    if (triggers.timing === 'immediate') {
      this.show();
      this.hasTriggered = true;
      return;
    }
    
    // Delay display
    if (triggers.timing === 'delay' && triggers.delaySeconds) {
      setTimeout(() => {
        if (this.shouldDisplay() && !this.hasTriggered) {
          this.show();
          this.hasTriggered = true;
        }
      }, triggers.delaySeconds * 1000);
      return;
    }
    
    // Scroll trigger
    if (triggers.timing === 'scroll' && triggers.scrollPercent) {
      this.setupScrollTrigger(triggers.scrollPercent);
    }
    
    // Exit intent
    if (triggers.exitIntent) {
      this.setupExitIntentTrigger();
    }
  }

  /**
   * Setup scroll-based trigger
   */
  setupScrollTrigger(scrollPercent) {
    const checkScroll = () => {
      if (this.hasTriggered) return;
      
      const scrolled = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      
      if (scrolled >= scrollPercent && this.shouldDisplay()) {
        this.show();
        this.hasTriggered = true;
        window.removeEventListener('scroll', checkScroll);
      }
    };
    
    window.addEventListener('scroll', checkScroll);
  }

  /**
   * Setup exit intent trigger
   */
  setupExitIntentTrigger() {
    const handleMouseLeave = (e) => {
      if (this.hasTriggered) return;
      
      if (e.clientY <= 0 && this.shouldDisplay()) {
        this.show();
        this.hasTriggered = true;
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
    
    document.addEventListener('mouseleave', handleMouseLeave);
  }

  /**
   * Check if popup should display based on frequency and targeting rules
   */
  shouldDisplay() {
    // TEST MODE: Check for test mode setting (bypasses all checks)
    if (this.config.testMode === true) {
      console.log('[Klaviyo Popup] Test mode enabled - bypassing all checks');
      return true;
    }
    
    // TEST MODE: Check for preview parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('klaviyo_preview') === 'true') {
      console.log('[Klaviyo Popup] Preview mode - bypassing all checks');
      return true;
    }
    
    // Check completion cookie
    if (this.getCookie('klaviyo_popup_completed')) {
      console.log('[Klaviyo Popup] Already completed');
      return false;
    }

    // Email was successfully submitted (saved step 3+); do not show acquisition popup again
    try {
      const saved = parseInt(localStorage.getItem(KLAVIYO_FLOW_LS_STEP), 10);
      if (Number.isFinite(saved) && saved >= 3) {
        console.log('[Klaviyo Popup] Email already captured — not showing popup');
        return false;
      }
    } catch {
      /* ignore */
    }
    
    // Check dismissal frequency
    const dismissedTime = this.getCookie('klaviyo_popup_dismissed');
    if (dismissedTime) {
      const daysSinceDismiss = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      const dismissedDays = this.config.frequency.dismissedDays || 7;
      
      if (daysSinceDismiss < dismissedDays) {
        console.log('[Klaviyo Popup] Dismissed recently');
        return false;
      }
    }
    
    // Check device targeting
    if (!this.matchesDeviceTarget()) {
      console.log('[Klaviyo Popup] Device target not matched');
      return false;
    }
    
    // Check audience targeting
    if (!this.matchesAudienceTarget()) {
      console.log('[Klaviyo Popup] Audience target not matched');
      return false;
    }
    
    return true;
  }

  /**
   * Check if current device matches targeting rules
   */
  matchesDeviceTarget() {
    const devices = this.config.targeting.devices || ['mobile', 'desktop', 'tablet'];
    const width = window.innerWidth;
    
    const isMobile = width < 768;
    const isTablet = width >= 768 && width < 1024;
    const isDesktop = width >= 1024;
    
    if (isMobile && devices.includes('mobile')) return true;
    if (isTablet && devices.includes('tablet')) return true;
    if (isDesktop && devices.includes('desktop')) return true;
    
    return false;
  }

  /**
   * Check if current user matches audience targeting rules
   */
  matchesAudienceTarget() {
    const audience = this.config.targeting.audience || ['new', 'returning'];
    
    // Check if returning visitor (has visitor cookie)
    const isReturning = this.getCookie('klaviyo_visitor') || 
                       document.cookie.includes('_shopify') || 
                       document.cookie.includes('cart');
    
    if (isReturning && audience.includes('returning')) return true;
    if (!isReturning && audience.includes('new')) return true;
    if (audience.includes('all')) return true;
    
    return false;
  }

  /**
   * Show the popup
   */
  show() {
    if (!this.overlay) return;

    // Remember what had focus so it can be restored when the dialog closes
    this._focusReturnEl = document.activeElement;

    this.overlay.style.display = 'flex';
    // Trigger reflow for animation
    this.overlay.offsetHeight;
    this.overlay.classList.add('klaviyo-popup-visible');

    [this.ids.emailForm, this.ids.smsForm].forEach((id) => {
      const form = document.getElementById(id);
      const btn = form?.querySelector('button[type="submit"]');
      if (btn) btn.disabled = false;
    });

    // Prevent body scroll
    document.body.classList.add('klaviyo-popup-open');

    // Move focus into the dialog (close button is always present and stable across steps)
    const closeBtn = this.overlay.querySelector('.klaviyo-popup-close');
    (closeBtn || this.getFocusableElements()[0])?.focus();

    // Track impression
    this.trackEvent('Popup Viewed', {
      step: this.currentStep
    });

    console.log('[Klaviyo Popup] Displayed');
  }

  /**
   * Hide the popup
   */
  hide() {
    if (!this.overlay) return;

    this.overlay.classList.remove('klaviyo-popup-visible');

    setTimeout(() => {
      this.overlay.style.display = 'none';
      document.body.classList.remove('klaviyo-popup-open');
    }, 400);

    // Return focus to whatever opened the dialog
    if (this._focusReturnEl && typeof this._focusReturnEl.focus === 'function') {
      this._focusReturnEl.focus();
    }
    this._focusReturnEl = null;

    console.log('[Klaviyo Popup] Hidden');
  }

  /**
   * Check if popup is currently visible
   */
  isVisible() {
    return this.overlay && this.overlay.classList.contains('klaviyo-popup-visible');
  }

  /**
   * Go to a specific step
   */
  goToStep(stepNumber) {
    if (stepNumber < 1 || stepNumber > this.totalSteps) return;
    
    // Hide current step
    const currentStepEl = document.getElementById(this.stepId(this.currentStep));
    if (currentStepEl) {
      currentStepEl.classList.remove('klaviyo-step-active');
    }

    // Show new step
    this.currentStep = stepNumber;
    const newStepEl = document.getElementById(this.stepId(this.currentStep));
    if (newStepEl) {
      newStepEl.classList.add('klaviyo-step-active');

      // Keep the dialog's accessible name pointed at the heading that's actually visible
      const heading = newStepEl.querySelector('.klaviyo-heading');
      if (heading && this.overlay) {
        if (!heading.id) heading.id = `klaviyo-popup-heading-step${stepNumber}${this.idSuffix}`;
        this.overlay.setAttribute('aria-labelledby', heading.id);
      }
    }

    if (stepNumber === 3) {
      requestAnimationFrame(() => {
        this.ensureAliaPhoneField().catch(() => {});
      });
    }

    // Track step view
    this.trackEvent('Popup Step Viewed', {
      step: this.currentStep
    });
    
    console.log(`[Klaviyo Popup] Moved to step ${this.currentStep}`);
  }

  /**
   * Load libphonenumber-js (ESM) for parse/validate/E.164 — used with native country select + flag UI.
   */
  loadPhoneLib() {
    if (this._phoneLibLoadPromise) return this._phoneLibLoadPromise;
    this._phoneLibLoadPromise = import(
      /* webpackIgnore: true */
      'https://cdn.jsdelivr.net/npm/libphonenumber-js@1.11.14/+esm'
    )
      .then((mod) => {
        const m = mod || {};
        const pick =
          typeof m.parsePhoneNumberFromString === 'function'
            ? m
            : m.default && typeof m.default.parsePhoneNumberFromString === 'function'
              ? m.default
              : null;
        this._phoneLib = pick;
        return this._phoneLib;
      })
      .catch((err) => {
        console.warn('[Klaviyo Popup] libphonenumber-js failed to load:', err);
        this._phoneLib = null;
        return null;
      });
    return this._phoneLibLoadPromise;
  }

  _regionDisplayName(iso2) {
    if (!iso2 || iso2 === 'ZZ') return 'International';
    if (KLAVIYO_PHONE_COUNTRY_LABELS[iso2]) return KLAVIYO_PHONE_COUNTRY_LABELS[iso2];
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(iso2);
    } catch {
      return iso2;
    }
  }

  _getPhoneCountryRegionCodes() {
    return KLAVIYO_PHONE_COUNTRY_CODES;
  }

  _populatePhoneCountrySelect(select) {
    const codes = this._getPhoneCountryRegionCodes();
    const sorted = [...new Set(codes)]
      .filter((c) => c && c !== 'ZZ')
      .sort((a, b) =>
        this._regionDisplayName(a).localeCompare(this._regionDisplayName(b), undefined, { sensitivity: 'base' })
      );

    const frag = document.createDocumentFragment();
    const intlOpt = document.createElement('option');
    intlOpt.value = 'ZZ';
    intlOpt.textContent = 'International';
    frag.appendChild(intlOpt);

    for (const code of sorted) {
      const opt = document.createElement('option');
      opt.value = code;
      opt.textContent = this._regionDisplayName(code);
      frag.appendChild(opt);
    }
    select.appendChild(frag);
  }

  /**
   * Alia-style phone: invisible native country select over flag, chevron, tel input; libphonenumber for parse/sync.
   */
  async ensureAliaPhoneField() {
    const select = document.getElementById(this.ids.phoneCountry);
    const input = document.getElementById(this.ids.phoneInput);
    if (!select || !input) return;

    if (!this._aliaPhoneDomReady) {
      this._aliaPhoneDomReady = true;
      this._populatePhoneCountrySelect(select);

      const defaultRaw = (this.config.phoneDefaultCountry || 'CA').toString().trim().toUpperCase();
      const defaultIso = defaultRaw.length === 2 ? defaultRaw : 'CA';
      const has = (v) => Array.from(select.options).some((o) => o.value === v);
      select.value = has(defaultIso) ? defaultIso : has('US') ? 'US' : 'ZZ';

      const countryEl = select.closest('.PhoneInputCountry');
      const img = countryEl && countryEl.querySelector('.PhoneInputCountryIconImg');
      const flagBase = 'https://purecatamphetamine.github.io/country-flag-icons/3x2/';
      const globeSvg =
        'data:image/svg+xml;charset=utf-8,' +
        encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 24" width="36" height="24"><rect fill="#e5e7eb" width="36" height="24"/><ellipse cx="18" cy="12" rx="10" ry="6" fill="none" stroke="#64748b" stroke-width="1.2"/><path fill="none" stroke="#64748b" stroke-width="1" d="M8 12h20M18 4c-4 4-4 16 0 20M18 4c4 4 4 16 0 20"/></svg>'
        );

      const updateFlag = () => {
        if (!img) return;
        const iso = select.value;
        if (iso === 'ZZ') {
          img.src = globeSvg;
          img.alt = 'International';
          img.onerror = null;
          return;
        }
        img.src = `${flagBase}${iso}.svg`;
        img.alt = this._regionDisplayName(iso) || iso;
        img.onerror = () => {
          img.onerror = null;
          img.src = globeSvg;
        };
      };

      this._phoneCountrySync = () => {
        const parse = this._phoneLib && this._phoneLib.parsePhoneNumberFromString;
        if (!parse || !input.value.trim()) return;
        const raw = input.value.trim();
        const defaultCountry = select.value === 'ZZ' ? undefined : select.value;
        try {
          const parsed = parse(raw, defaultCountry);
          if (parsed && parsed.country) {
            const c = String(parsed.country).toUpperCase();
            if (c !== select.value && Array.from(select.options).some((o) => o.value === c)) {
              select.value = c;
              updateFlag();
            }
          }
        } catch {
          /* ignore */
        }
      };

      select.addEventListener('change', updateFlag);
      input.addEventListener('input', () => this._phoneCountrySync && this._phoneCountrySync());
      input.addEventListener('paste', () => requestAnimationFrame(() => this._phoneCountrySync && this._phoneCountrySync()));

      updateFlag();
      this._aliaPhoneDomReady = true;
    }

    await this.loadPhoneLib();
    if (typeof this._phoneCountrySync === 'function') {
      this._phoneCountrySync();
    }
  }

  /**
   * Handle email form submission
   */
  async handleEmailSubmit(e) {
    e.preventDefault();
    
    const emailInput = document.getElementById(this.ids.emailInput);
    const errorEl = document.getElementById(this.ids.emailError);
    const email = emailInput.value.trim();
    
    if (!email) {
      this.showError(emailInput, errorEl, 'Email is required');
      return;
    }
    
    if (!this.validateEmail(email)) {
      this.showError(emailInput, errorEl, 'Invalid email');
      return;
    }
    
    // Clear error
    this.clearError(emailInput, errorEl);
    
    // Store email
    this.userData.email = email;

    const emailForm = document.getElementById(this.ids.emailForm);
    const submitBtn =
      (e.submitter && e.submitter.type === 'submit' ? e.submitter : null) ||
      emailForm?.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    try {
      // Submit to Klaviyo API
      await this.submitToKlaviyo(
        {
          email: email,
          properties: {
            product_interest: this.userData.product_interest || 'not_specified'
          },
          subscriptions: {
            email: {
              marketing: {
                consent: 'SUBSCRIBED'
              }
            }
          }
        },
        this.getEmailListId(),
        'klaviyo-popup-email'
      );
      
      // Track conversion
      this.trackEvent('Email Captured', { email: email });

      // Intelligems custom trigger (A/B test variant only) — same igEvents pattern as
      // the cart urgency tooltip (assets/component-cart-urgency-tooltip.js)

        window.igEvents = window.igEvents || [];
        window.igEvents.push({ event: 'klaviyo-popup-v2:email-submitted' });
      

      this.persistEmailComplete(email);

      // Go to next step (SMS)
      this.goToStep(3);
      
      console.log('[Klaviyo Popup] Email captured successfully');
      
    } catch (error) {
      if (submitBtn) submitBtn.disabled = false;
      this.showError(emailInput, errorEl, 'Something went wrong. Please try again.');
      console.error('[Klaviyo Popup] Email submission error:', error);
    }
  }

  /**
   * Handle SMS form submission
   */
  async handleSMSSubmit(e) {
    e.preventDefault();

    await this.ensureAliaPhoneField();

    const phoneInput = document.getElementById(this.ids.phoneInput);
    const errorEl = document.getElementById(this.ids.phoneError);
    const phone = phoneInput ? phoneInput.value.trim() : '';

    // Validate phone
    if (!this.validatePhone(phone)) {
      this.showError(phoneInput, errorEl, 'Please enter a valid phone number');
      return;
    }
    
    // Clear error
    this.clearError(phoneInput, errorEl);
    
    // Store phone
    this.userData.phone = this.formatPhone(phone);

    const smsForm = document.getElementById(this.ids.smsForm);
    const submitBtn =
      (e.submitter && e.submitter.type === 'submit' ? e.submitter : null) ||
      smsForm?.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    try {
      // Submit to Klaviyo API
      await this.submitToKlaviyo(
        {
          email: this.userData.email,
          phone_number: this.userData.phone,
          properties: {
            product_interest: this.userData.product_interest || 'not_specified'
          },
          subscriptions: {
            sms: {
              marketing: {
                consent: 'SUBSCRIBED'
              }
            }
          }
        },
        this.getSmsListId(),
        'klaviyo-popup-sms'
      );
      
      // Track conversion
      this.trackEvent('SMS Captured', { phone: this.userData.phone });
      
      this.persistReachedConfirmation();

      // Go to confirmation step
      this.goToStep(4);
      
      console.log('[Klaviyo Popup] SMS captured successfully');
      
    } catch (error) {
      if (submitBtn) submitBtn.disabled = false;
      this.showError(phoneInput, errorEl, 'Something went wrong. Please try again.');
      console.error('[Klaviyo Popup] SMS submission error:', error);
    }
  }

  /**
   * Handle survey selection
   */
  async handleSurveySelection(e) {
    const interest = e.currentTarget.dataset.interest;
    
    if (!interest) return;

    if (interest === 'none') {
      this.trackEvent('Survey Skipped', { step: this.currentStep });
      this.handleDismiss();
      return;
    }
    
    // Store interest
    this.userData.product_interest = interest;
    
    // Track survey selection
    this.trackEvent('Survey Completed', { interest: interest });
    
    // Go to email step
    this.goToStep(2);
    this.persistSurveyComplete(interest);
    
    console.log('[Klaviyo Popup] Survey selection:', interest);
  }

  /**
   * Submit data to Klaviyo API
   */
  getEmailListId() {
    return this.config.klaviyoEmailListId || this.config.klaviyoListId || '';
  }

  getSmsListId() {
    return this.config.klaviyoSmsListId || this.config.klaviyoListId || '';
  }

  identifyProfile(profileAttributes) {
    if (!window._learnq) return;

    const identifyPayload = {};
    if (profileAttributes.email) {
      identifyPayload.$email = profileAttributes.email;
    }
    if (profileAttributes.phone_number) {
      identifyPayload.$phone_number = profileAttributes.phone_number;
    }
    if (profileAttributes.properties && typeof profileAttributes.properties === 'object') {
      Object.assign(identifyPayload, profileAttributes.properties);
    }

    if (Object.keys(identifyPayload).length) {
      window._learnq.push(['identify', identifyPayload]);
    }
  }

  async submitToKlaviyo(profileAttributes, listId, customSource) {
    if (!listId) {
      console.warn(`[Klaviyo Popup] No list ID configured for ${customSource}; falling back to profile identify only.`);
      this.identifyProfile(profileAttributes);
      return;
    }

    const url = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.config.klaviyoPublicKey)}`;
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId
            }
          }
        }
      }
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json',
        'revision': '2026-04-15'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok && response.status !== 202) {
      let errorMessage = 'Klaviyo subscription failed.';

      try {
        const errorData = await response.json();
        const detail = errorData && errorData.errors && errorData.errors[0] && errorData.errors[0].detail;
        if (detail) {
          errorMessage = detail;
        }
      } catch (error) {
        console.warn('[Klaviyo Popup] Unable to parse Klaviyo error response.', error);
      }

      throw new Error(errorMessage);
    }

    this.identifyProfile(profileAttributes);
    return response;
  }

  /**
   * Handle popup dismissal (close button, overlay, ESC, No thanks, Maybe next time)
   */
  handleDismiss() {
    this.clearFlowLocalStorage();

    const completedDays = this.config.frequency.completedDays || 365;
    this.setCookie('klaviyo_popup_completed', 'true', completedDays);

    this.trackEvent('Popup Dismissed', {
      step: this.currentStep
    });

    this.hide();

    console.log('[Klaviyo Popup] Dismissed permanently');
  }

  /**
   * Complete the popup flow
   */
  complete() {
    this.clearFlowLocalStorage();

    // Set completion cookie (never show again)
    const completedDays = this.config.frequency.completedDays || 365;
    this.setCookie('klaviyo_popup_completed', 'true', completedDays);
    
    // Set visitor cookie for audience targeting
    this.setCookie('klaviyo_visitor', 'true', 365);
    
    // Track completion
    this.trackEvent('Popup Completed', {
      email: this.userData.email,
      phone: this.userData.phone,
      product_interest: this.userData.product_interest
    });
    
    // Hide popup
    this.hide();
    
    console.log('[Klaviyo Popup] Completed');
  }

  /**
   * Show validation error
   */
  showError(inputEl, errorEl, message) {
    if (inputEl) {
      inputEl.classList.add('klaviyo-input-error');
      inputEl.setAttribute('aria-invalid', 'true');
      const phoneWrap = inputEl.closest('.PhoneInput');
      if (phoneWrap) phoneWrap.classList.add('klaviyo-PhoneInput--error');
    }
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  /**
   * Clear validation error
   */
  clearError(inputEl, errorEl) {
    if (inputEl) {
      inputEl.classList.remove('klaviyo-input-error');
      inputEl.setAttribute('aria-invalid', 'false');
      const phoneWrap = inputEl.closest('.PhoneInput');
      if (phoneWrap) phoneWrap.classList.remove('klaviyo-PhoneInput--error');
    }
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  /**
   * Validate email format
   */
  validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  /**
   * Validate phone format
   */
  validatePhone(phone) {
    const raw = (phone != null ? String(phone) : '').trim();
    if (!raw) return false;

    const select = document.getElementById(this.ids.phoneCountry);
    const parse = this._phoneLib && this._phoneLib.parsePhoneNumberFromString;
    if (typeof parse === 'function') {
      try {
        const defaultCountry = select && select.value !== 'ZZ' ? select.value : undefined;
        let parsed = parse(raw, defaultCountry);
        if (parsed && parsed.isValid()) return true;
        parsed = parse(raw);
        if (parsed && parsed.isValid()) return true;
        return false;
      } catch {
        /* fall through */
      }
    }

    const digits = raw.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  }

  /**
   * Format phone to E.164 format
   */
  formatPhone(phone) {
    const raw = (phone != null ? String(phone) : '').trim();
    const select = document.getElementById(this.ids.phoneCountry);
    const parse = this._phoneLib && this._phoneLib.parsePhoneNumberFromString;
    if (typeof parse === 'function' && raw) {
      try {
        const defaultCountry = select && select.value !== 'ZZ' ? select.value : undefined;
        let parsed = parse(raw, defaultCountry);
        if (parsed && parsed.isValid()) return parsed.format('E.164');
        parsed = parse(raw);
        if (parsed && parsed.isValid()) return parsed.format('E.164');
      } catch {
        /* fall through */
      }
    }

    const digits = raw.replace(/\D/g, '');

    if (digits.length === 10) {
      return `+1${digits}`;
    }

    if (digits.length === 11 && digits[0] === '1') {
      return `+${digits}`;
    }

    return digits ? `+${digits}` : '';
  }

  /**
   * Track event (using Klaviyo SDK if available)
   */
  trackEvent(eventName, properties = {}) {
    if (window._learnq) {
      window._learnq.push(['track', eventName, properties]);
    }
    
    // Also track to console in development
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
      console.log('[Klaviyo Event]', eventName, properties);
    }
  }

  /**
   * Set cookie
   */
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  /**
   * Get cookie
   */
  getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initKlaviyoPopup);
} else {
  initKlaviyoPopup();
}

function initKlaviyoPopup() {
  // Configuration - should be set via Liquid template
  const config = window.klaviyoPopupConfig || {
    klaviyoPublicKey: '',
    klaviyoEmailListId: '',
    klaviyoSmsListId: '',
    klaviyoListId: '',
    triggers: {
      timing: 'delay',
      delaySeconds: 5,
      scrollPercent: 50,
      exitIntent: true
    },
    targeting: {
      devices: ['mobile', 'desktop', 'tablet'],
      audience: ['new', 'returning']
    },
    frequency: {
      dismissedDays: 7,
      completedDays: 365
    },
    phoneDefaultCountry: 'ca'
  };
  
  // Check if config is valid
  if (config.klaviyoPublicKey) {
    const popup = new KlaviyoPopupController(config);
    popup.init();

    // Expose globally for debugging
    window.klaviyoPopup = popup;
  } else {
    console.warn('[Klaviyo Popup] Public API key not configured');
  }

  // A/B test variant: a second popup instance, namespaced with its own element ids,
  // so it can coexist on the page with the one above (the A/B tool toggles which is visible).
  const configV2 = window.klaviyoPopupConfigV2;
  if (configV2 && configV2.klaviyoPublicKey) {
    const popupV2 = new KlaviyoPopupController({ ...configV2, idSuffix: '-v2' });
    popupV2.init();

    window.klaviyoPopupV2 = popupV2;
  }
}
